import { useAlert } from '@ads/components-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  ClassModuleFormProvider,
  ClassModuleFormTemplate,
  type ClassModuleFormValues,
  resolveModuleReturnPath,
  useSaveClassModule,
} from '@/features/class-module-management';

const searchSchema = z.object({
  /** Quando `grade-config`, o retorno (salvar/cancelar) volta para a tela de Nota/Frequência. */
  returnTo: z.literal('grade-config').optional(),
  /** Turma a pré-vincular no novo módulo (vinda do grade-config). */
  classStudentId: z.coerce.number().int().positive().optional(),
  classStudentName: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/class-modules/create/')({
  validateSearch: searchSchema,
  component: ClassModuleCreatePage,
});

/** Página de cadastro de um novo módulo de turma. */
export function ClassModuleCreatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('classModuleManagement');
  const { alert } = useAlert();
  const { save, isPending } = useSaveClassModule();
  const { returnTo, classStudentId, classStudentName } = Route.useSearch();

  const returnPath = resolveModuleReturnPath(returnTo);

  // Quando aberto a partir do grade-config, a turma selecionada já vem vinculada.
  const initialValues: ClassModuleFormValues | undefined = classStudentId
    ? {
        companyId: null,
        modality: null,
        description: '',
        dateStart: '',
        dateFinish: '',
        linkedClasses: [{ classStudentId, name: classStudentName ?? '', course: '' }],
        disciplines: [],
      }
    : undefined;

  return (
    <ClassModuleFormProvider isEdit={false} {...(initialValues ? { initialValues } : {})}>
      <ClassModuleFormTemplate
        title={t('breadcrumb.create')}
        isSubmitting={isPending}
        onSubmit={payload =>
          new Promise<void>((resolve, reject) => {
            save(payload, {
              onSuccess: () => resolve(),
              onError: () => {
                alert({ alertType: 'warning', title: t('toast.error') });
                reject(new Error('save failed'));
              },
            });
          })
        }
        onSaved={() => {
          alert({ alertType: 'success', title: t('toast.createSuccess') });
          void navigate({ to: returnPath });
        }}
        onCancel={() => void navigate({ to: returnPath })}
      />
    </ClassModuleFormProvider>
  );
}
